/**
 * One poller per thing being watched, however many things are watching it.
 *
 * This is the polling store the listening surface was built on, generalised: what a
 * feed is playing and what the product has established about a feed are both "a live
 * value, addressed by feed id, that several parts of the screen read at once". They
 * differ only in what is fetched and how often it is worth asking again, so those are
 * the two arguments, and everything else — one request per key, every watcher told,
 * the last known value kept when a read fails, exponential backoff, nothing polled
 * while nothing watches it or while the tab is in the background — is written once.
 */

export interface ResourceState<T> {
  data: T | null;
  error: string | null;
  /** True until the first answer for this key, success or failure. */
  loading: boolean;
  updatedAt: number | null;
  failures: number;
}

export interface ResourceDelayInput<T> {
  snapshot: T | null;
  /** Consecutive failed reads. Zero on any success. */
  failures: number;
  now: number;
}

export interface ResourceStoreOptions<T> {
  fetcher: (key: string, signal: AbortSignal) => Promise<T>;
  delay: (input: ResourceDelayInput<T>) => number;
  now?: () => number;
}

interface Entry<T> {
  state: ResourceState<T>;
  listeners: Set<() => void>;
  timer: ReturnType<typeof setTimeout> | null;
  controller: AbortController | null;
}

function initialState<T>(): ResourceState<T> {
  return {
    data: null,
    error: null,
    loading: true,
    updatedAt: null,
    failures: 0,
  };
}

export class ResourceStore<T> {
  readonly #entries = new Map<string, Entry<T>>();
  readonly #fetcher: (key: string, signal: AbortSignal) => Promise<T>;
  readonly #delay: (input: ResourceDelayInput<T>) => number;
  readonly #now: () => number;
  readonly #initial: ResourceState<T> = initialState<T>();

  constructor(options: ResourceStoreOptions<T>) {
    this.#fetcher = options.fetcher;
    this.#delay = options.delay;
    this.#now = options.now ?? (() => Date.now());
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") this.refreshAll();
        else this.#suspendAll();
      });
    }
  }

  getState(key: string): ResourceState<T> {
    return this.#entries.get(key)?.state ?? this.#initial;
  }

  subscribe(key: string, listener: () => void): () => void {
    const entry = this.#entry(key);
    entry.listeners.add(listener);
    if (entry.listeners.size === 1) void this.#poll(key);
    return () => {
      entry.listeners.delete(listener);
      if (entry.listeners.size === 0) this.#suspend(key);
    };
  }

  refresh(key: string): void {
    if (this.#entries.get(key)?.listeners.size) void this.#poll(key);
  }

  refreshAll(): void {
    for (const key of this.#entries.keys()) this.refresh(key);
  }

  /** Forgets a key outright — what a deleted feed's state should become. */
  forget(key: string): void {
    this.#suspend(key);
    this.#entries.delete(key);
  }

  #entry(key: string): Entry<T> {
    let entry = this.#entries.get(key);
    if (!entry) {
      entry = {
        state: this.#initial,
        listeners: new Set(),
        timer: null,
        controller: null,
      };
      this.#entries.set(key, entry);
    }
    return entry;
  }

  #emit(key: string, state: ResourceState<T>): void {
    const entry = this.#entry(key);
    entry.state = state;
    for (const listener of entry.listeners) listener();
  }

  async #poll(key: string): Promise<void> {
    const entry = this.#entry(key);
    entry.controller?.abort();
    if (entry.timer !== null) clearTimeout(entry.timer);
    entry.timer = null;
    const controller = new AbortController();
    entry.controller = controller;
    try {
      const data = await this.#fetcher(key, controller.signal);
      if (controller.signal.aborted) return;
      this.#emit(key, {
        data,
        error: null,
        loading: false,
        updatedAt: this.#now(),
        failures: 0,
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      const previous = this.getState(key);
      this.#emit(key, {
        // What was last true is kept on screen; a failed read is reported beside it
        // rather than blanking a value that is, as far as anyone knows, still true.
        data: previous.data,
        error: error instanceof Error ? error.message : String(error),
        loading: false,
        updatedAt: previous.updatedAt,
        failures: previous.failures + 1,
      });
    }
    this.#schedule(key);
  }

  #schedule(key: string): void {
    const entry = this.#entry(key);
    if (entry.listeners.size === 0) return;
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      return;
    }
    const delay = this.#delay({
      snapshot: entry.state.data,
      failures: entry.state.failures,
      now: this.#now(),
    });
    entry.timer = setTimeout(() => void this.#poll(key), delay);
  }

  #suspend(key: string): void {
    const entry = this.#entries.get(key);
    if (!entry) return;
    if (entry.timer !== null) clearTimeout(entry.timer);
    entry.timer = null;
    entry.controller?.abort();
    entry.controller = null;
  }

  #suspendAll(): void {
    for (const key of this.#entries.keys()) this.#suspend(key);
  }
}
